// src/services/api/AiChatService.js
// AIチャット（「AIに聞く」）機能のAPIクライアント。
// レスポンスがSSE（text/event-stream）のため、BaseApiServiceのaxiosインスタンスは使えず、
// getPdf（fetch直叩き）と同様に認証ヘッダー・X-New-Tokenを手動で再現する。

import { handleSessionExpiredResponse } from '../base/BaseApiService';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// X-New-Token を fetch からも取得するためのユーティリティ（BaseApiServiceのhandleNewTokenと同様）
const handleNewToken = (response) => {
  const newToken = response.headers.get('x-new-token');
  if (newToken) {
    localStorage.setItem('token', newToken);
  }
};

/**
 * SSEストリームの `data: {json}\n\n` 行をパースしてイベントオブジェクトの配列を返す
 * @param {string} chunkText - デコード済みのチャンク文字列（複数イベントを含みうる）
 * @returns {Array<Object>} パースされたイベントオブジェクトの配列
 */
export const parseSSEChunk = (chunkText) => {
  const events = [];
  // SSEの1イベントは "\n\n" 区切り。1チャンクに複数イベントが含まれることがあるため分割してループする。
  const parts = chunkText.split('\n\n');
  
  for (const part of parts) {
    const line = part.trim();
  
    // "data: {...}" 以外の行（コメント行やイベント名行等）は本実装では扱わないため無視する。
    if (!line.startsWith('data:')) continue;
    const jsonText = line.slice('data:'.length).trim();
  
    // 空のdata行（キープアライブ等）はスキップ。
    if (!jsonText) continue;
  
    try {
      events.push(JSON.parse(jsonText));
    } catch (error) {
      // 1件のパース失敗でストリーム全体を止めないよう、ログのみ出して読み飛ばす。
      console.error('SSEイベントのパースに失敗:', error, jsonText);
    }
  }
  return events;
};

/**
 * AIチャットSSEエンドポイントへPOSTリクエストを送る。
 * BaseApiServiceのaxiosインスタンスはSSEレスポンスを扱えないため、fetchを直接使う。
 * @param {string} url - リクエスト先URL
 * @param {Object} requestBody - リクエストボディ（sendAiChatMessageの引数をそのまま渡す）
 * @param {AbortSignal} [signal] - リクエスト中断用のシグナル
 * @returns {Promise<Response>} fetchのレスポンス
 */
const fetchAiChatStream = async (url, requestBody, signal) => {
  try {
    return await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
      signal,
    });
  } catch (error) {
    console.error('AI Chat request error:', error);
    throw error;
  }
};

/**
 * レスポンスが異常系（`!response.ok`）の場合、サーバーが返したエラー詳細を含む Error を投げる。
 * 正常系（`response.ok`）の場合は何もしない。
 * @param {Response} response - fetchのレスポンス
 * @returns {Promise<void>}
 */
const assertResponseOk = async (response) => {
  if (response.ok) return;

  let errorData = null;
  let detail = `AIチャットの送信に失敗しました: ${response.status} ${response.statusText}`;

  try {
    errorData = await response.json();
    if (errorData?.detail) {
      detail = errorData.detail;
    }
  } catch (parseError) {
    // JSONでないエラーレスポンスはデフォルトメッセージのまま
  }

  // セッション切れ（401）は、他の画面と同じログアウト・リダイレクト処理に揃える。
  // BaseApiServiceのaxiosインターセプターと同様、以降の処理は進めない（後続の.then/.catchを実行させない）。
  if (response.status === 401) {
    handleSessionExpiredResponse({
      response: { status: response.status, data: errorData },
      config: { url: response.url },
    });
    return new Promise(() => {});
  }

  const error = new Error(detail);
  error.status = response.status;
  throw error;
};

/**
 * SSEレスポンスボディを最後まで読み取り、イベントが確定するたびに onEvent を呼び出す。
 * `done` を実際の終了条件とする `while (!done)` ループでチャンクを読み進める
 *
 * 想定されるレスポンスボディ（生のSSEテキスト）の例:
 * ```
 * data: {"event": "progress", "node": "receive_query"}
 *
 * data: {"event": "progress", "node": "run_kb_search"}
 *
 * data: {"event": "answer_delta", "content": "特約の"}
 *
 * data: {"event": "answer_delta", "content": "免責事項は次の通りです。"}
 *
 * data: {"event": "answer", "conversation_id": "c-123", "content": "特約の免責事項は次の通りです。", "sources": [{"blob_name": "policy.pdf", "pages": [3], "heading": "免責事項"}]}
 *
 * ```
 * このうち `answer_delta` は1回のreader.read()で複数件まとめて届くこともあれば、
 * 1件のJSONが2回のチャンクに分割されて届くこともある（bufferで結合してから確定させる理由）。
 *
 * @param {Response} response - `assertResponseOk` を通過済みのレスポンス
 * @param {Function} onEvent - SSEイベント受信ごとに呼ばれるコールバック（event: {event, ...}）
 * @returns {Promise<void>}
 */
const pumpStream = async (response, onEvent) => {
  
  // response.bodyはUint8Arrayのチャンクを返すストリームなので、UTF-8文字列に変換するためのreader/decoderを用意する。
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  
  // 直前のreadで受信した「イベント区切り(\n\n)に満たない未処理分」を次のreadまで保持しておくバッファ。
  let buffer = '';
  
  // reader.read()が {done: true} を返すまでの読み取り継続フラグ。
  let done = false;
  while (!done) {
    
    // reader.read()はPromiseで、解決時に{value, done}を返す。valueはUint8Arrayのチャンク。
    const result = await reader.read();

    // ストリーム終端に達したら、このreadにはvalueが無いためデコードせずループを抜ける。
    done = result.done;
    if (done) break;

    // stream: true は「マルチバイト文字がチャンク境界で分断される可能性」に対応するためのオプション。
    // 未完成のバイト列は decoder 内部に保持され、次回の decode 呼び出しで続きとして結合される。
    // CRLF（\r\n\r\n）で区切って送信するサーバーでも "\n\n" 境界検出が機能するよう、ここで改行コードをLFに正規化する。
    // \rと\nがチャンク境界で分割されるケースに対応するため、連結後のbuffer全体に対して正規化する
    // （新規デコード分だけをreplaceすると、前チャンク末尾の\rと次チャンク先頭の\nが正規化されずに残る）。
    buffer = (buffer + decoder.decode(result.value, { stream: true })).replace(/\r\n/g, '\n');

    // 完全なイベント区切り（\n\n）が来た分だけ処理し、残りはbufferに保持する
    // 区切りがまだ1つも無い＝イベントがまだ完成していないので、次のチャンクを待つ。
    const lastBoundary = buffer.lastIndexOf('\n\n');
    if (lastBoundary === -1) continue;

    // 最後の区切りまでを「完成したイベント群」として確定し、
    // 残り（次の未完成イベントの先頭）はbufferに戻す。
    const completeChunk = buffer.slice(0, lastBoundary);
    buffer = buffer.slice(lastBoundary + 2);

    // 確定した分だけパースし、イベントごとに呼び出し元へ即時通知する
    // （ストリーミング表示のため逐次コールバック）。
    for (const event of parseSSEChunk(completeChunk)) {
      onEvent(event);
    }
  }

  // ストリーム終端後、区切り待ちのままbufferに残った最後のイベントをflushする。
  // （サーバーが最後のイベントの後に "\n\n" を送らずコネクションを閉じた場合の救済処理）
  if (buffer.trim()) {
    for (const event of parseSSEChunk(buffer)) {
      onEvent(event);
    }
  }
};

/**
 * AIチャットへメッセージを送信し、SSEイベントを逐次コールバックに通知する
 * @param {Object} requestBody - AiChatMessageRequest相当のリクエストボディ
 * @param {string|null} requestBody.conversation_id - 会話ID（新規会話時はnull）
 * @param {string} requestBody.blob_name - 表示中文書のBlob名
 * @param {string} requestBody.index_name - 検索対象のインデックス名
 * @param {number} requestBody.insurance_type - 保険種別（1:生保, 2:損保）
 * @param {string} [requestBody.document_type] - 文書種別（例: "しおり・約款"。ドメインコンテキスト選択に使用）
 * @param {string} requestBody.message - 質問文または聞き返しへの回答
 * @param {Function} onEvent - SSEイベント受信ごとに呼ばれるコールバック（event: {event, ...}）
 * @param {AbortSignal} [signal] - リクエスト中断用のシグナル
 * @returns {Promise<void>}
 */
export const sendAiChatMessage = async (requestBody, onEvent, signal) => {
  const url = `${API_BASE_URL}/ai-chat/messages`;

  // fetchでSSEストリームを取得する
  const response = await fetchAiChatStream(url, requestBody, signal);

  // レスポンスヘッダーで新しいアクセストークンが発行されていれば保存する（BaseApiServiceのhandleNewTokenと同様）。
  handleNewToken(response);

  // エラーレスポンスならここで例外を投げ、以降のストリーム読み取りは行わない。
  await assertResponseOk(response);

  // SSEストリームを最後まで読み取り、イベントが確定するたびに onEvent を呼び出す
  await pumpStream(response, onEvent);
};

export default {
  sendAiChatMessage,
  parseSSEChunk,
};
